import type { SavedView, SavedViewConfig, SavedViewSlice } from '@app/src/types/savedViews'
import { SavedViewVisibility } from '@app/src/types/savedViews'

/** The five MRT state slices a view can capture, in the order the create popover lists them. */
export const SAVED_VIEW_SLICE_OPTIONS: { slice: SavedViewSlice; labelIntlId: string }[] = [
  { slice: 'columnFilters', labelIntlId: 'savedViews.slice.filters' },
  { slice: 'sorting', labelIntlId: 'savedViews.slice.sorting' },
  { slice: 'columnVisibility', labelIntlId: 'savedViews.slice.columnsShown' },
  { slice: 'columnOrder', labelIntlId: 'savedViews.slice.columnOrder' },
  { slice: 'columnPinning', labelIntlId: 'savedViews.slice.pinnedColumns' },
]

export const SAVED_VIEW_SLICES: SavedViewSlice[] = SAVED_VIEW_SLICE_OPTIONS.map(option => option.slice)

const isPlainObject = (value: unknown): boolean => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const isStringArray = (value: unknown): boolean =>
  Array.isArray(value) && value.every(entry => typeof entry === 'string')

// Filters and sorting are lists of { id, ... } entries that the table reads the id off.
const isArrayOfEntriesWithId = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.every(entry => isPlainObject(entry) && typeof (entry as { id?: unknown }).id === 'string')

const IS_VALID_SLICE: Record<SavedViewSlice, (value: unknown) => boolean> = {
  columnVisibility: isPlainObject,
  columnOrder: isStringArray,
  columnFilters: isArrayOfEntriesWithId,
  columnPinning: isPlainObject,
  sorting: isArrayOfEntriesWithId,
}

/** A snapshot of the live table state, keyed by slice. */
export type SavedViewState = Record<SavedViewSlice, unknown>

/** Setters keyed by slice; only the setters for included slices are invoked on apply. */
export type SavedViewSetters = Record<SavedViewSlice, (value: unknown) => void>

/** Picks only the ticked slices from the live table state into a view config. */
export const buildViewConfig = (state: SavedViewState, includedSlices: SavedViewSlice[]): SavedViewConfig => {
  const config: SavedViewConfig = {}
  SAVED_VIEW_SLICES.forEach(slice => {
    if (includedSlices.includes(slice)) {
      config[slice] = state[slice]
    }
  })
  return config
}

export const serializeConfig = (config: SavedViewConfig): string => JSON.stringify(config)

/** Keeps only the known slices that have the shape the table expects, so one bad slice can't break the rest. */
const pickValidSlices = (parsed: Record<string, unknown>): SavedViewConfig => {
  const config: SavedViewConfig = {}
  SAVED_VIEW_SLICES.forEach(slice => {
    if (slice in parsed && IS_VALID_SLICE[slice](parsed[slice])) {
      config[slice] = parsed[slice]
    }
  })
  return config
}

/** Defensive parse: any non-object or malformed input yields an empty config (applies nothing). */
export const parseConfig = (raw: string): SavedViewConfig => {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return pickValidSlices(parsed as Record<string, unknown>)
    }
    return {}
  } catch {
    return {}
  }
}

/** Applies a config by calling only the setters for slices present in it, leaving the rest untouched. */
export const applyViewConfig = (config: SavedViewConfig, setters: SavedViewSetters): void => {
  SAVED_VIEW_SLICES.forEach(slice => {
    if (slice in config) {
      setters[slice](config[slice])
    }
  })
}

/** Splits views into the two nav sections: Private (mine) and Shared (org-wide, any creator). */
export const groupViews = (views: SavedView[]): { private: SavedView[]; shared: SavedView[] } => ({
  private: views.filter(view => view.visibility === SavedViewVisibility.Private),
  shared: views.filter(view => view.visibility === SavedViewVisibility.SharedToOrg),
})
